import React from 'react';
import { stateLabel } from './invoiceStatus';

/**
 * A new version of an earlier invoice (GET /api/review/invoices/{id}: `revision`). The banner says what was found
 * (the same number, or a reply to the email that brought the earlier one) and what the email reads as, and how that
 * was decided: simple parsing of the email, or the model an admin chose in AI settings. The choice belongs to the
 * approver: Replace the earlier invoice (it is kept as Replaced; its prices come off and stock moves by the
 * difference) or Keep both, with a reason.
 */
const MATCH = {
  same_number: 'has the same invoice number as',
  reply: 'was sent as a reply to the email that brought',
  dispute_reference: 'answers our dispute over',
};
const HOW = { rules: 'simple parsing of the email', model: 'the AI model chosen in AI settings', human: 'a person' };

function earlierName(revision) {
  const e = revision?.earlier || {};
  return `invoice ${e.number || ''}`.trim();
}

export function RevisionBanner({ revision }) {
  if (!revision?.earlier) return null;
  const e = revision.earlier;
  const facts = [e.date, e.total ? `£${e.total}` : null, stateLabel(e.state)].filter(Boolean).join(', ');
  return (
    <div className="rounded-xl bg-accent-50 border border-accent-300 px-4 py-3 text-accent-800 space-y-1" data-testid="revision-banner">
      <p><strong>A new version?</strong> This invoice {MATCH[revision.match] || MATCH.same_number} {earlierName(revision)} ({facts}).</p>
      {revision.says && (
        <p className="text-sm" data-testid="revision-reading">
          The email {revision.says} {earlierName(revision)}
          {revision.signals?.length ? ` ("${revision.signals.join('", "')}")` : ''}, read by {HOW[revision.authority] || HOW.rules}.
        </p>
      )}
    </div>
  );
}

export function RevisionChoice({ revision, action, onAction, reason, onReason }) {
  const name = revision?.earlier ? earlierName(revision) : 'an invoice already received';
  return (
    <fieldset className="space-y-2" data-testid="revision-choice">
      <legend className="text-danger">This may be a new version of {name}. What should happen to it?</legend>
      <label className="flex items-start gap-2">
        <input type="radio" name="revision-action" checked={action === 'replace'} onChange={() => onAction('replace')}
          className="mt-1" data-testid="revision-replace" />
        <span><strong>Replace it with this one.</strong> The earlier invoice is kept as Replaced; its prices come off
          and stock moves by the difference.</span>
      </label>
      <label className="flex items-start gap-2">
        <input type="radio" name="revision-action" checked={action === 'keep_both'} onChange={() => onAction('keep_both')}
          className="mt-1" data-testid="revision-keep" />
        <span><strong>Keep both.</strong> They are separate invoices.</span>
      </label>
      {action === 'keep_both' && (
        <label className="block">
          <span>Say why both should count:</span>
          <textarea value={reason} onChange={(e) => onReason(e.target.value)} rows={2} data-testid="duplicate-reason-input"
            className="mt-1 w-full rounded-lg border border-surface-border p-2 text-ink" />
        </label>
      )}
    </fieldset>
  );
}
